import React from 'react'
import { observer } from 'mobx-react-lite'
import { useVirtualizer } from '@tanstack/react-virtual'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'

import { MerchantLogo, Text } from '@mxenabled/mxui'

import {
  Loader,
  SearchBox,
  useGlobalCopyStore,
  useGlobalUiStore,
  useMerchantStore,
} from '../../common'

const NAME_PATTERN = /^[ a-zA-ZÀ-ÿ0-9]+$/
const MAX_LENGTH = 50
const ROW_HEIGHT = 65 // List items have a minHeight of 64, so we add 1px for the borders so they don't disappear on hover

export interface MerchantLookupProps {
  height?: string | number
  onSelect: (guid: string) => void
}

const MerchantLookup: React.FC<MerchantLookupProps> = ({
  height = 'calc(100dvh - 120px)',
  onSelect,
}) => {
  const { isLoading, merchants, loadMerchants } = useMerchantStore()
  const { common: copy } = useGlobalCopyStore()
  const { isCopyLoaded } = useGlobalUiStore()

  const [searchValue, setSearchValue] = React.useState('')

  React.useEffect(() => {
    loadMerchants().finally()
  }, [loadMerchants])

  const handleSelected = (guid: string) => {
    onSelect(guid)
  }

  const handleSearch = async (value: string) => {
    if ((!value || NAME_PATTERN.test(value)) && value.length <= MAX_LENGTH) {
      setSearchValue(value)
      await loadMerchants(value)
    }
  }

  const parentRef = React.useRef<HTMLDivElement>(null)
  const rowVirtualizer = useVirtualizer({
    count: merchants.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  })

  React.useEffect(() => rowVirtualizer.measure(), [merchants])

  if (!isCopyLoaded) {
    return <Loader />
  }

  return (
    <Stack className="mx-mrt-lookup">
      <SearchBox
        ariaLabel={copy.search_aria}
        cancelAriaLabel={copy.cancel_search_aria}
        onChange={handleSearch}
        placeholder={copy.search_placeholder}
        searchValue={searchValue}
      />

      <Paper sx={{ boxShadow: 'none' }}>
        <List>
          <Box height={height} overflow="auto" ref={parentRef}>
            <Box height={rowVirtualizer.getTotalSize()} position="relative">
              {isLoading && <Loader label="Loading..." size={32} />}
              {!isLoading &&
                rowVirtualizer.getVirtualItems().length > 0 &&
                rowVirtualizer.getVirtualItems().map((row) => {
                  const merchant = merchants[row.index]

                  return (
                    <Box
                      height={ROW_HEIGHT}
                      key={merchant.guid}
                      position="absolute"
                      sx={{ transform: `translateY(${row.start}px)` }}
                      width="100%"
                    >
                      <ListItem disableGutters={true} disablePadding={true}>
                        <ListItemButton onClick={() => handleSelected(merchant.guid)}>
                          <ListItemAvatar>
                            <MerchantLogo categoryGuid="" merchantGuid={merchant.guid} />
                          </ListItemAvatar>
                          <ListItemText primary={merchant.name} />
                        </ListItemButton>
                      </ListItem>
                      <Divider sx={{ ml: 24 }} />
                    </Box>
                  )
                })}
            </Box>
            {!merchants.length && (
              <Stack alignItems="center" my={24}>
                <Text bold={true} color="secondary" variant="body2">
                  {copy.search_zero_state}
                </Text>
              </Stack>
            )}
          </Box>
        </List>
      </Paper>
    </Stack>
  )
}

export default observer(MerchantLookup)
